// hooks/useResponseHandlers.ts
import { useCallback } from 'react';

import { GeneratedContentTypeEnum } from '../components/savi-content-panels/types';
import { useChatOverlayContext } from '../context/chatOverlayContext';
import { useIspmErrorContext } from '../context/ispmErrorContext';
import { ErrorTypeEnum } from '../data-layer/types';

/**
 * A custom hook that handles different types of responses and updates the chat overlay context accordingly.
 * Each handler manages specific types of content or error responses by modifying the context state.
 *
 * @returns {Object} - An object containing various response handlers to manage UI updates based on the response type.
 */
const useResponseHandlers = () => {
  const { updateChatOverlayState } = useChatOverlayContext();
  const { updateIspmErrorState } = useIspmErrorContext();

  /**
   * Handles a random response by hiding the generated content panel.
   */
  const handleRandomResponse = useCallback(() => {
    updateChatOverlayState('showGeneratedContent', false);
  }, [updateChatOverlayState]);

  /**
   * Handles a high-confidence response that requires a table display.
   * Updates the content panel kind to 'table'.
   */
  const handleHighConfidenceTableResponse = useCallback(() => {
    updateChatOverlayState('contentPanelKind', 'table');
  }, [updateChatOverlayState]);

  /**
   * Handles a high-confidence response that requires a chart display.
   * Updates the content panel kind to 'chart'.
   */
  const handleHighConfidenceChartResponse = useCallback(() => {
    updateChatOverlayState('contentPanelKind', 'chart');
  }, [updateChatOverlayState]);

  /**
   * Handles a sample data chart response by updating the content panel kind to 'chart'.
   */
  const handleSampleDataChartResponse = useCallback(() => {
    updateChatOverlayState('contentPanelKind', 'chart');
  }, [updateChatOverlayState]);

  /**
   * Handles a low-confidence response that is displayed in a tabular format.
   * Updates the content panel kind to 'lowConfidence'.
   */
  const handleLowConfidenceTabularResponse = useCallback(() => {
    updateChatOverlayState('contentPanelKind', 'lowConfidence');
  }, [updateChatOverlayState]);

  /**
   * Handles a low-confidence response that is displayed as a chart.
   * Updates the content panel kind to 'lowConfidence'.
   */
  const handleLowConfidenceChartResponse = useCallback(() => {
    updateChatOverlayState('contentPanelKind', 'lowConfidence');
  }, [updateChatOverlayState]);

  /**
   * Handles a sample data table response by updating the content panel kind to 'table'.
   */
  const handleSampleDataTableResponse = useCallback(() => {
    updateChatOverlayState('contentPanelKind', 'table');
  }, [updateChatOverlayState]);

  /**
   * Handles a generic error response by hiding the generated content panel.
   */
  const handleErrorResponse = useCallback(
    (errorType: ErrorTypeEnum) => {
      updateChatOverlayState('showGeneratedContent', false);
      updateIspmErrorState('errorType', errorType);

      if (errorType === ErrorTypeEnum.System) {
        updateChatOverlayState('inputDisabled', true);
      } else if (errorType === ErrorTypeEnum.SQL) {
        updateChatOverlayState('showGeneratedContent', true);
        updateChatOverlayState(
          'contentPanelKind',
          GeneratedContentTypeEnum.SQL_ERROR
        );
      }
    },
    [updateChatOverlayState, updateIspmErrorState]
  );

  return {
    handleRandomResponse,
    handleHighConfidenceTableResponse,
    handleSampleDataTableResponse,
    handleErrorResponse,
    handleHighConfidenceChartResponse,
    handleSampleDataChartResponse,
    handleLowConfidenceTabularResponse,
    handleLowConfidenceChartResponse,
  };
};

export default useResponseHandlers;
